import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  HighlightOff as HighlightOffIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Cancel as CancelIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import { useKeycloak } from '../context/KeycloakContext';
import queueService from '../services/queueService';
import taskService from '../services/taskService';
import codexService from '../services/codexService';
import {
  dataUrlToObjectUrl,
} from '../utils/codexPreviewUtils';
import { format } from 'date-fns';

const STATUS_TYPES = ['UNASSIGNED_OR_OPEN', 'ASSIGNED'];
const PAGE_SIZE = 20;

// Status chip styling
const getStatusColor = (status) => {
  switch (status) {
    case 'completed': return 'success';
    case 'processing': return 'info';
    case 'pending': return 'warning';
    case 'error': return 'error';
    case 'rejected': return 'error';
    default: return 'default';
  }
};

const PREVIEW_SIZE = 72; // ~1.5x rispetto all'altezza standard della riga
const PREVIEW_STORAGE_PREFIX = 'documentiPreviewCache:';

const resolvePreviewStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch (error) {
    console.warn('Local storage non disponibile', error);
    return null;
  }
};

// Component for Queue's Activities
const QueueActivities = () => {
  const { queueId } = useParams();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const userInfo = keycloak.tokenParsed || {};
  const isAdmin = userInfo.groups?.some(group => group.toLowerCase().includes('admin'));
  const isEditorOrAdmin = userInfo.realm_access?.roles?.includes('editor') || isAdmin;
  
  // Queue data
  const [queue, setQueue] = useState(null);
  
  // Activities state
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [previewState, setPreviewState] = useState({});

  const loadMoreRef = useRef(null);
  const statusPageRef = useRef({ UNASSIGNED_OR_OPEN: 0, ASSIGNED: 0 });
  const statusHasMoreRef = useRef({ UNASSIGNED_OR_OPEN: true, ASSIGNED: true });
  const previewCacheRef = useRef(new Map());
  const previewStateRef = useRef({});
  const previewStorageRef = useRef(undefined);
  const isMountedRef = useRef(true);
  
  // Selected activity for detail view
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // State for unclaim dialog
  const [unclaimDialogOpen, setUnclaimDialogOpen] = useState(false);
  const [unclaimReason, setUnclaimReason] = useState('');


  useEffect(() => {
    previewStateRef.current = previewState;
  }, [previewState]);

  useEffect(() => () => {
    isMountedRef.current = false;
    previewCacheRef.current.forEach((entry) => {
      if (entry?.objectUrl) {
        URL.revokeObjectURL(entry.objectUrl);
      }
    });
    previewCacheRef.current.clear();
  }, []);

  const getPreviewStorage = useCallback(() => {
    if (previewStorageRef.current !== undefined) {
      return previewStorageRef.current;
    }
    previewStorageRef.current = resolvePreviewStorage();
    return previewStorageRef.current;
  }, []);

  const updatePreviewState = useCallback((codexId, updates) => {
    if (!codexId || !isMountedRef.current) {
      return;
    }
    const key = String(codexId);
    setPreviewState((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        ...updates,
      },
    }));
  }, []);

  const loadPreviewForCodex = useCallback(async (codexId) => {
    if (!codexId) {
      return;
    }

    const codexKey = String(codexId);

    setPreviewState((prev) => {
      if (!isMountedRef.current) {
        return prev;
      }
      const existing = prev[codexKey];
      if (existing?.loading || existing?.loaded) {
        return prev;
      }
      return {
        ...prev,
        [codexKey]: {
          ...(existing || {}),
          loading: true,
        },
      };
    });

    const cached = previewCacheRef.current.get(codexKey);
    if (cached?.objectUrl) {
      updatePreviewState(codexKey, {
        previewUrl: cached.objectUrl,
        previewMimeType: cached.mimeType,
        loading: false,
        loaded: true,
      });
      return;
    }

    const storage = getPreviewStorage();
    if (storage) {
      try {
        const raw = storage.getItem(`${PREVIEW_STORAGE_PREFIX}${codexKey}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.dataUrl) {
            const { objectUrl, mimeType } = dataUrlToObjectUrl(parsed.dataUrl);
            previewCacheRef.current.set(codexKey, {
              objectUrl,
              mimeType: parsed.mimeType ?? mimeType,
              dataUrl: parsed.dataUrl,
              fileName: parsed.fileName,
            });
            updatePreviewState(codexKey, {
              previewUrl: objectUrl,
              previewMimeType: parsed.mimeType ?? mimeType,
              loading: false,
              loaded: true,
            });
            return;
          }
        }
      } catch (storageError) {
        console.warn(`Anteprima cache non valida per il Codex ${codexKey}`, storageError);
      }
    }

    try {
      // Use the thumbnail API endpoint
      const response = await codexService.getThumbnail(codexId);

      if (!response || !response.thumbnail) {
        updatePreviewState(codexKey, {
          previewUrl: null,
          previewMimeType: null,
          loading: false,
          loaded: true,
        });
        return;
      }

      const thumbnailDataUrl = response.thumbnail;

      // Convert dataUrl to objectUrl for rendering
      const { objectUrl, mimeType } = dataUrlToObjectUrl(thumbnailDataUrl);

      const previewData = {
        objectUrl,
        mimeType,
        dataUrl: thumbnailDataUrl,
      };

      previewCacheRef.current.set(codexKey, previewData);

      updatePreviewState(codexKey, {
        previewUrl: previewData.objectUrl,
        previewMimeType: previewData.mimeType,
        loading: false,
        loaded: true,
      });

      if (storage && previewData.dataUrl) {
        const payload = {
          dataUrl: previewData.dataUrl,
          mimeType: previewData.mimeType,
          storedAt: Date.now(),
        };
        try {
          storage.setItem(`${PREVIEW_STORAGE_PREFIX}${codexKey}`, JSON.stringify(payload));
        } catch (storageError) {
          console.warn('Impossibile memorizzare la cache delle anteprime', storageError);
        }
      }
    } catch (previewError) {
      console.error(`Errore nel caricamento dell'anteprima per il Codex ${codexKey}`, previewError);
      updatePreviewState(codexKey, {
        previewUrl: null,
        previewMimeType: null,
        loading: false,
        loaded: true,
        error: true,
      });
    }
  }, [getPreviewStorage, updatePreviewState]);

  useEffect(() => {
    activities.forEach((activity) => {
      const codexId = activity?.codexId || activity?.codex?.id;
      if (!codexId) {
        return;
      }
      const key = String(codexId);
      const info = previewState[key] || previewStateRef.current[key];
      if (!info || (!info.loaded && !info.loading)) {
        loadPreviewForCodex(key);
      }
    });
  }, [activities, previewState, loadPreviewForCodex]);

  // Function to fetch queue details
  const fetchQueueDetails = useCallback(async () => {
    try {
      if (!queueId) {
        setError('Invalid queue ID');
        return;
      }
      const queueData = await queueService.getById(queueId);
      setQueue(queueData);
    } catch (err) {
      console.error('Error fetching queue details:', err);
      setError('Failed to load queue details. Please try again.');
    }
  }, [queueId]);

  // Function to fetch activities
  const fetchActivities = useCallback(async ({ reset = false } = {}) => {
    if (!queueId) {
      setError('Invalid queue ID');
      return;
    }

    if (reset) {
      statusPageRef.current = { UNASSIGNED_OR_OPEN: 0, ASSIGNED: 0 };
      statusHasMoreRef.current = { UNASSIGNED_OR_OPEN: true, ASSIGNED: true };
      setActivities([]);
      setHasMore(true);
      setLoadingMore(false);
    }

    const statusesToFetch = STATUS_TYPES.filter(
      (status) => statusHasMoreRef.current[status]
    );

    if (statusesToFetch.length === 0) {
      setHasMore(false);
      return;
    }

    const responses = await Promise.all(
      statusesToFetch.map(async (status) => {
        const pageIndex = statusPageRef.current[status];
        const response = await queueService.getQueueTasks(
          queueId,
          pageIndex,
          PAGE_SIZE,
          'createdAt,desc',
          status
        );
        return { status, response };
      })
    );

    const newTasks = responses.flatMap(({ response }) => response.tasks || []);

    responses.forEach(({ status, response }) => {
      statusPageRef.current[status] += 1;
      const pagination = response.pagination || {};
      const hasMoreForStatus = Boolean(response.tasks?.length) && !(pagination.isLast ?? true);
      statusHasMoreRef.current[status] = hasMoreForStatus;
    });

    setHasMore(Object.values(statusHasMoreRef.current).some(Boolean));

    const codexIdsToEnsure = new Set();

    setActivities((prevActivities) => {
      const base = reset ? [] : prevActivities;
      const merged = [...base, ...newTasks];
      const dedupedMap = new Map();

      merged.forEach((task) => {
        if (task?.id) {
          dedupedMap.set(task.id, task);
        }
      });

      const deduped = Array.from(dedupedMap.values());
      deduped.sort((a, b) => {
        const aDate = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bDate = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bDate - aDate;
      });

      deduped.forEach((task) => {
        const codexId = task?.codexId || task?.codex?.id;
        if (!codexId) {
          return;
        }
        const key = String(codexId);
        const previewInfo = previewStateRef.current[key];
        if (!previewInfo || (!previewInfo.loaded && !previewInfo.loading)) {
          codexIdsToEnsure.add(key);
        }
      });

      return deduped;
    });

    codexIdsToEnsure.forEach((codexId) => {
      loadPreviewForCodex(codexId);
    });
  }, [queueId, loadPreviewForCodex]);

  const handleRefresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await fetchQueueDetails();
      await fetchActivities({ reset: true });
    } catch (err) {
      console.error('Error refreshing activities:', err);
      setError('Failed to load activities. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fetchActivities]);

  useEffect(() => {
    if (!queueId) {
      return;
    }

    let isMounted = true;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        await fetchQueueDetails();
        if (isMounted) {
          await fetchActivities({ reset: true });
        }
      } catch (err) {
        console.error('Error loading queue activities:', err);
        if (isMounted) {
          setError('Failed to load activities. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [queueId, fetchQueueDetails, fetchActivities]);

  useEffect(() => {
    activities.forEach((activity) => {
      const codexId = activity?.codexId || activity?.codex?.id;
      if (!codexId) {
        return;
      }
      const existing = previewStateRef.current[codexId];
      if (existing?.loaded || existing?.loading) {
        return;
      }
      loadPreviewForCodex(codexId);
    });
  }, [activities, loadPreviewForCodex]);

  const handleLoadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    try {
      setLoadingMore(true);
      setError(null);
      await fetchActivities();
    } catch (err) {
      console.error('Error loading more activities:', err);
      setError('Failed to load more activities. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  }, [fetchActivities, loading, loadingMore, hasMore]);

  useEffect(() => {
    if (!hasMore) {
      return;
    }

    const sentinel = loadMoreRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0.1
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [handleLoadMore, hasMore]);
  
  // Handle view activity details
  const handleViewActivity = (activity) => {
    setSelectedActivity(activity);
    setDetailDialogOpen(true);
  };
  
  // Handle close detail dialog
  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedActivity(null);
  };
  
  // Handle open unclaim dialog
  const handleOpenUnclaimDialog = (taskId) => {
    setSelectedActivity(activities.find(activity => activity.id === taskId)); // Set selected task for context
    setUnclaimReason(''); // Clear previous reason
    setUnclaimDialogOpen(true);
  };

  // Handle close unclaim dialog
  const handleCloseUnclaimDialog = () => {
    setUnclaimDialogOpen(false);
    setUnclaimReason('');
    setSelectedActivity(null);
  };

  // Handle confirm unclaim
  const handleConfirmUnclaim = async () => {
    if (!selectedActivity || !unclaimReason.trim()) {
      setError('Please provide a reason to unclaim the task.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await taskService.unclaimTask(selectedActivity.id, unclaimReason);
      await fetchActivities({ reset: true });
      handleCloseUnclaimDialog();
    } catch (err) {
      console.error('Error unclaiming task:', err);
      setError('Failed to unclaim task. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle view process
  const handleViewProcess = (processId) => {
    navigate(`/process/${processId}`);
  };

  // Handle view codex details
  const handleViewCodex = (codexId) => {
    navigate(`/codex/${codexId}`);
  };

  // Handle claim task and navigate to codex detail
  const handleClaimAndViewCodex = async (activity) => {
    try {
      setLoading(true);
      setError(null);
      
      let response;

      if (!activity.assignee && activity.status !== 'COMPLETED') {
        // Task is not assigned - claim it first
        response = await taskService.claimTask(activity.id, queueId);
      } else if (activity.status === 'COMPLETED' || activity.status === 'REJECTED') {
        // Task is completed or rejected - get task details from history
        response = await taskService.getTaskById(activity.id, true);
        response.history = true;
      } else {
        // Task is already assigned to current user - get task details directly
        response = await taskService.getTaskById(activity.id);
      }
      
      // Navigate to codex detail page using the codexId from the returned task
      const codexId = response.codexId;
      if (codexId) {
        navigate(`/codex/${codexId}`, { state: { taskId: response.history?null:activity.id, fromQueueActivities: true } });
      } else {
        setError('Could not retrieve codex information from task');
      }
    } catch (err) {
      console.error('Error accessing codex details:', err);
      setError('Failed to access codex details. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Function to update action status
  const handleUpdateActionStatus = async (actionId, newStatus) => {
    try {
      setLoading(true);
      await queueService.updateActionStatus(actionId, newStatus);
      
      // Update the local state if the action is in the current view
      setActivities(activities.map(activity => {
        if (activity.id === actionId) {
          return {
            ...activity,
            status: newStatus,
            executed_at: ['completed', 'failed'].includes(newStatus) ? new Date().toISOString() : activity.executed_at
          };
        }
        return activity;
      }));
      
      // If we're viewing the details of this action, update the selected activity
      if (selectedActivity && selectedActivity.id === actionId) {
        setSelectedActivity({
          ...selectedActivity,
          status: newStatus,
          executed_at: ['completed', 'failed'].includes(newStatus) ? new Date().toISOString() : selectedActivity.executed_at
        });
      }
      
      setLoading(false);
      
      // Show success message
      setError(null);
    } catch (err) {
      console.error(`Error updating action status:`, err);
      setError(`Failed to update action status: ${err.message}`);
      setLoading(false);
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
    } catch (e) {
      console.error('Date formatting error:', e);
      return dateString;
    }
  };
  
  const handleClaimTask = async (taskId) => {
    try {
      setLoading(true);
      setError(null);
      await taskService.claimTask(taskId, queueId);
      await fetchActivities({ reset: true });
    } catch (err) {
      console.error('Error claiming task:', err);
      setError('Failed to claim task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      setLoading(true);
      setError(null);
      await taskService.completeTask(taskId);
      await fetchActivities({ reset: true });
      handleCloseDetailDialog();
    } catch (err) {
      console.error('Error completing task:', err);
      setError('Failed to complete task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectTask = async (taskId) => {
    try {
      setLoading(true);
      setError(null);
      await taskService.rejectTask(taskId);
      await fetchActivities({ reset: true });
      handleCloseDetailDialog();
    } catch (err) {
      console.error('Error rejecting task:', err);
      setError('Failed to reject task. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0 }}>
            {queue ? queue.code : 'Attività'}
          </Typography>
          {queue && (
            <Typography variant="subtitle1" color="text.secondary">
              Type: {queue.type} | Priority: {queue.priority}
            </Typography>
          )}
        </Box>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>
      
      {/* Error message if any */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Activities table */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <TableContainer>
          <Table sx={{ minWidth: 650, tableLayout: 'fixed' }} size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                <TableCell sx={{ width: PREVIEW_SIZE + 24 }}>Anteprima</TableCell>
                <TableCell>Documento</TableCell>
                <TableCell>Descrizione</TableCell>
                <TableCell>Assegnatario</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && activities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : activities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      Nessun task disponibile.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                activities.map((activity) => {
                  const codexId = activity.codexId || activity.codex?.id;
                  const codexKey = codexId ? String(codexId) : null;
                  const previewInfo = codexKey ? previewState[codexKey] : undefined;
                  const previewUrl = previewInfo?.previewUrl;
                  const previewLoading = Boolean(previewInfo?.loading);
                  const previewIconColor = previewInfo?.error ? 'error' : 'disabled';

                  return (
                    <TableRow
                      key={activity.id}
                      hover
                      sx={{ cursor: codexId ? 'pointer' : 'default' }}
                      onClick={() => {
                        if (codexId) {
                          handleClaimAndViewCodex(activity);
                        }
                      }}
                    >
                      <TableCell sx={{ width: PREVIEW_SIZE + 24 }}>
                        <Box
                          sx={{
                            width: PREVIEW_SIZE,
                            height: PREVIEW_SIZE,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'grey.100',
                            borderRadius: 1,
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          {previewLoading ? (
                            <CircularProgress size={24} />
                          ) : previewUrl ? (
                            <Box
                              component="img"
                              src={previewUrl}
                              alt={activity.codexCode || 'Anteprima documento'}
                              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                          ) : (
                            <InsertDriveFileIcon color={previewIconColor} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{activity.codexCode || '-'}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: '300px',
                          wordWrap: 'break-word',
                          whiteSpace: 'normal'
                        }}
                      >
                        {activity.codexDescription || '-'}
                      </TableCell>
                      <TableCell>
                        {activity.assignee ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={activity.assignee}
                              color="primary"
                              size="small"
                            />
                            {activity.assignee === userInfo.preferred_username && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleOpenUnclaimDialog(activity.id);
                                }}
                              >
                                Rilascia
                              </Button>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Non assegnato
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              <TableRow ref={loadMoreRef}>
                <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                  {loadingMore ? <CircularProgress size={24} /> : null}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Activity detail dialog: WARNING - this never opens */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedActivity && (
          <>
            <DialogTitle>
              Dettaglio Task
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {selectedActivity.name}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Processo
                  </Typography>
                  <Typography variant="body1">
                    {selectedActivity.processName}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Assegnatario
                  </Typography>
                  <Typography variant="body1">
                    {selectedActivity.assignee || 'Non assegnato'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Stato
                  </Typography>
                  {(() => {
                    const selectedActivityChipLabel = selectedActivity.status === 'ASSIGNED' ? 'Assegnato' :
                                                      (selectedActivity.status === 'COMPLETED' ? 'Completato' :
                                                      (selectedActivity.status === 'REJECTED' ? 'Rifiutato' : 'Non assegnato'));
                    const selectedActivityChipColor = getStatusColor(selectedActivity.status ? selectedActivity.status.toLowerCase() : 'default');

                    return (
                      <Chip
                        label={selectedActivityChipLabel}
                        color={selectedActivityChipColor}
                        size="small"
                      />
                    );
                  })()}
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Data creazione
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedActivity.createdAt)}
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              {isEditorOrAdmin && (
                <>
                  {!selectedActivity.assignee && (
                    <Button 
                      color="primary"
                      onClick={() => handleClaimTask(selectedActivity.id)}
                      startIcon={<AssignmentIcon />}
                    >
                      Prendi in carico
                    </Button>
                  )}
                  {(isAdmin || selectedActivity.assignee === userInfo.preferred_username) && (
                    <>
                      <Button 
                        color="success"
                        onClick={() => handleCompleteTask(selectedActivity.id)}
                        startIcon={<CheckCircleOutlineIcon />}
                      >
                        Completa
                      </Button>
                      <Button 
                        color="error"
                        onClick={() => handleRejectTask(selectedActivity.id)}
                        startIcon={<CancelIcon />}
                      >
                        Rifiuta
                      </Button>
                      <Button 
                        color="warning"
                        onClick={() => handleOpenUnclaimDialog(selectedActivity.id)}
                        startIcon={<HighlightOffIcon />}
                      >
                        Rilascia
                      </Button>
                    </>
                  )}
                </>
              )}
              <Button onClick={handleCloseDetailDialog}>Chiudi</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Unclaim Reason Dialog */}
      <Dialog open={unclaimDialogOpen} onClose={handleCloseUnclaimDialog}>
        <DialogTitle>Rilascia Task</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Per favore, inserisci una nota per il rilascio del task {selectedActivity && selectedActivity.name ? selectedActivity.name : ''}.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="unclaimReason"
            label="Nota di rilascio"
            type="text"
            fullWidth
            variant="outlined"
            value={unclaimReason}
            onChange={(e) => setUnclaimReason(e.target.value)}
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnclaimDialog} color="primary">
            Annulla
          </Button>
          <Button onClick={handleConfirmUnclaim} color="primary" variant="contained">
            Rilascia Task
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QueueActivities; 
